import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import FieldWrapper from '@shared/components/forms/inputs/field-wrapper/field-wrapper.vue';
import { Component, Watch } from 'vue-property-decorator';
import VSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { BookingEvent, PublicBookingEventData } from '@bookings/interfaces/booking-event';
import moment, { Moment } from 'moment';
import { ParsedEvent } from '@bookings/interfaces/calendar';
import ResourceCalendar from '../resource-calendar/resource-calendar.vue';
import Datepicker from '@shared/components/forms/inputs/datepicker/datepicker.vue';
import PageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { extractOptions } from '@core/helpers/option-groups.helper';
import {
  Booking,
  BookingOrganizationForm,
  DaysInput,
  PublicBookingFormData,
} from '@bookings/interfaces/booking';
import { mixins } from 'vue-class-component';
import { ScheduleSlotRange } from '@bookings/interfaces/schedule-slot-range';
import PublicOrganizationBookingFormCalendarOptions from './public-organization-booking-form-options';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import { DateSelectArg } from '@fullcalendar/vue';
import TimeRange from '@shared/components/forms/inputs/time-range/time-range.vue';
import DatetimePickertpicker from '@shared/components/forms/inputs/datetimepicker/datetimepicker.vue';
import BookingEventOrganizationTableInput from '@shared/components/forms/inputs/booking-event-organization-table-input/booking-event-organization-table-input.vue';
import BpcFormProvider from '@shared/components/forms/form-provider.vue';
import { prepareToSave } from '@shared/helpers/process-entity';
import Select from '@shared/components/forms/inputs/select/select.vue';
import Time from '@shared/components/forms/inputs/time/time.vue';
import ButtonGroup from '@shared/components/forms/inputs/button-group/button-group.vue';
import Switch from '@shared/components/forms/inputs/switch/switch.vue';
import Input from '@shared/components/forms/inputs/input/input.vue';
import Textarea from '@shared/components/forms/inputs/textarea/textarea.vue';
import PublicOrganizationBookingCustomForm from '@bookings/components/public-organization-booking-form/public-organization-booking-custom-form.vue';
import { setProp } from '@shared/helpers/operators';

@Component({
  name: 'bpc-public-organization-booking-form',
  methods: { prepareToSave },
  components: {
    BpcFormProvider,
    'v-select': VSelect,
    'bpc-select': Select,
    'bpc-entity-select': EntitySelect,
    'bpc-field-wrapper': FieldWrapper,
    'bpc-resource-calendar': ResourceCalendar,
    'bpc-datepicker': Datepicker,
    'bpc-time': Time,
    'bpc-button-group': ButtonGroup,
    'bpc-switch': Switch,
    'bpc-input': Input,
    'bpc-textarea': Textarea,
    'bpc-page-title': PageTitle,
    'bpc-std-form': StdForm,
    'bpc-time-range': TimeRange,
    'bpc-datetimepicker': DatetimePickertpicker,
    'bc-booking-event-organization-table-input': BookingEventOrganizationTableInput,
    'bc-public-organization-custom-form': PublicOrganizationBookingCustomForm,
  },
})
export default class PublicOrganizationBookingForm extends mixins(
  PublicOrganizationBookingFormCalendarOptions,
) {
  $refs: {
    stdForm: InstanceType<typeof StdForm> & {
      saveEntity(): Promise<BPCGenericApiResponse>;
      formModel: any;
      onDeleteMediaFile(): void;
      searchEntity(): void;
      extraData: string | AnyEntity;
      formProvider: EntityFormProvider;
      prepareToSave(entity: any): AnyEntity;
    };
  };

  public entity: Booking = null;
  public formModel: Partial<BookingOrganizationForm> = {
    id: null,
    center_id: this.tenantId,
    repetitive: false,
    period_id: null,
    start_date: null,
    end_date: null,
    start_time: null,
    end_time: null,
    days: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
    bank_holidays: true,
    school_holidays: true,
    resources: [],
    resource_id: null,
    discipline_id: null,
    name: null,
    comment: null,
    bookingEvents: [],
  };
  public formStep = 1;
  public entityName = 'booking';
  public pluralEntityName = 'bookings';
  public permissionEntityNameToUse = 'bookings';
  public showConfirmationBookingForm: boolean = false;
  public i: number = 0;

  // PROVISIONAL //
  get tenantId(): number {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  resetFormModel() {
    this.formModel = {
      id: null,
      center_id: this.tenantId,
      repetitive: false,
      period_id: null,
      start_date: null,
      end_date: null,
      start_time: null,
      end_time: null,
      days: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
      bank_holidays: true,
      school_holidays: true,
      resources: [],
      resource_id: null,
      discipline_id: null,
      name: null,
      comment: null,
      bookingEvents: [],
    };
  }

  errorsObject: object = {};

  /**
   * Events
   */
  events: ParsedEvent[] = [];
  tmpEvents: ParsedEvent[] = [];
  excludedEvents: ParsedEvent[] = [];
  backgroundEvents: ParsedEvent[] = [];
  bookingData: PublicBookingFormData = null;
  bookingEventData: PublicBookingEventData = null;
  confirmedBookingEventData: Booking = null;
  currentContactBookings: BookingEvent[] = [];
  proposedBookingEvents: BookingEvent[] = [];

  /**
   * Vars for control if it's possible to do a booking event in the selected slot
   */
  minAndMaxDateCriterionError: boolean = false;
  minTimeBeforeTwoBookingsCriterionError: boolean = false;

  slotsChecked: boolean = false;
  slots = [];

  /**
   * Others attr
   */
  successText: string = 'global.generic.successful_action';
  optionsDebugMode = false;
  savingBooking: boolean = false;
  cancellingBooking: boolean = false;
  get readyToShowCalendar(): boolean {
    return this.selectedDate && this.resources.length > 0 && this.slotsChecked;
  }

  /* Lifecycle hooks */
  // created () { }
  async mounted() {
    await this.loadResources();
    await this.getSlotPricesCategories();
    await this.loadBookingOptions();
    await this.getAllEvents();
    this.checkSlotsAvailability();
  }
  // updated () { }
  // destroyed () { }

  /* Methods for manage modals */
  backToFormStep1() {
    this.formStep = 1;
  }
  pushToFormStep2() {
    this.formStep = 2;
  }

  manageSaveErrors(error) {
    if (error.response.status === 422) {
      if (error.response.data.errors) {
        // this.errorsObject = error.response.data.errors;
        Object.keys(error.response.data.errors).forEach(field => {
          const errorField = {
            error: error.response.data.errors[field][0],
            state: false,
            veeValidation: false,
          };
          this.$set(this.errorsObject, field, errorField);
        });
      }
    } else if (error.response.status === 420) {
      this.$emit('validation-warning', error);
    }
  }

  onConfirmBookingModal() {
    this.savingBooking = true;
    this.cleanTmpEvents();
    if (this.formStep === 1 && this.formModel.repetitive) {
      const basePath = this.$bpcApiRouteHelper.getEntityPath('booking');
      this.$bpcApi.post(basePath + '/public-search', this.formModel).subscribe(
        res => {
          setProp(this.formModel, 'bookingEvents', res.data.bookingEvents);
          this.proposedBookingEvents = res.data.bookingEvents;
          this.pushToFormStep2();
          this.savingBooking = false;
        },
        error => {
          this.manageSaveErrors(error);
          this.savingBooking = false;
        },
      );
    } else {
      this.savingBooking = false;
      this.saveBooking(this.formModel);
    }
  }

  onCloseConfirmBookingModal() {
    this.$bvModal.hide('booking-confirmation-modal');
    this.showConfirmationBookingForm = false;
    this.bookingData = null;
    this.cleanTmpEvents();
    this.resetFormModel();
    this.backToFormStep1();
    this.i++;
  }

  onCloseSuccessConfirmBookingModal() {
    this.$bvModal.hide('success-booking-confirmation-modal');
  }

  showBookingEventDetailModal() {
    this.$bvModal.show('booking-detail-modal');
  }

  onCloseDetailBookingEventModal() {
    this.$bvModal.hide('booking-detail-modal');
  }

  onCancelBookingEventModal() {
    const canBeCancelled = this.checkIfBookingCanBeCancelled(this.bookingEventData);
    if (canBeCancelled) {
      this.$bvModal
        .msgBoxConfirm(
          this.$t('global.modals.public_booking.cancellation.confirm_question').toString(),
          {
            title: this.$t('global.modals.public_booking.cancellation.title').toString(),
            cancelTitle: this.$t('global.buttons.close').toString(),
            okTitle: this.$t('global.buttons.confirm').toString(),
            okVariant: 'primary',
            cancelVariant: 'link',
          },
        )
        .then(value => {
          if (value) {
            this.cancellingBooking = true;
            this.cancelBookingEvent(this.bookingEventData);
          }
        })
        .catch(err => {
          // An error occurred
        });
    } else {
      const diffInMinutes = moment(
        `${this.bookingEventData.date} ${this.bookingEventData.start_time}`,
      ).diff(moment(), 'minutes');

      this.$bvModal.msgBoxOk(
        this.$t('global.modals.public_booking.cancellation.not_possible', {
          minTime: moment()
            .subtract(
              Math.abs(diffInMinutes - this.minTimeBeforeCancellationCriterionErrorMessage + 1),
              'minutes',
            )
            .format('DD/MM/YYYY HH:mm'),
        }).toString(),
        {
          title: this.$t('global.modals.public_booking.cancellation.title').toString(),
          okTitle: this.$t('global.buttons.close').toString(),
        },
      );
    }
  }

  showBookingNotPosibleModal() {
    this.$bvModal.show('booking-not-posible-modal');
  }

  onCloseNotPosibleModal() {
    this.$bvModal.hide('booking-not-posible-modal');
  }

  showBookingConfirmationModal() {
    this.showConfirmationBookingForm = true;
    this.$bvModal.show('booking-confirmation-modal');
  }

  /* Cancel event */
  get minTimeBeforeCancellation() {
    return 60 * (this.mergedBookingOptions?.min_time_before_cancellation || 0);
  }

  checkIfBookingCanBeCancelled(bookingEventData: PublicBookingEventData): boolean {
    const savedMergedBookingOptions = this.mergedBookingOptions;

    this.mergedBookingOptions = extractOptions(this.bookingOptions, {
      contacts: this.currentContactId,
      resources: bookingEventData.resourceId,
    });
    const diffInMinutes = moment(`${bookingEventData.date} ${bookingEventData.start_time}`).diff(
      moment(),
      'minutes',
    );
    const isValid = diffInMinutes >= this.minTimeBeforeCancellation;

    if (!isValid) {
      this.minTimeBeforeCancellationCriterionErrorMessage = this.minTimeBeforeCancellation;
    } else {
      this.minTimeBeforeCancellationCriterionErrorMessage = null;
    }

    this.mergedBookingOptions = savedMergedBookingOptions;

    return isValid;
  }

  async cancelBookingEvent(data: PublicBookingEventData) {
    const res = await this.$bpcApi
      .post(`${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/public/cancel`, data)
      .toPromise();

    this.$bvModal.hide('booking-cancellation-modal');
    this.$bvModal.msgBoxOk(
      this.$t('global.modals.public_booking.cancellation.success_body').toString(),
      {
        title: this.$t('global.modals.public_booking.cancellation.title').toString(),
        okTitle: this.$t('global.buttons.close').toString(),
        okVariant: 'secondary',
      },
    );
    this.onCloseDetailBookingEventModal();
    await this.getAllEvents();
    this.checkSlotsAvailability();
    this.cancellingBooking = false;
  }

  /* Load events */
  get eventQuery() {
    return {
      status: ['confirmed', 'requested'],
      page: 1,
      per_page: 200,
      orderBy: 'date',
      orderDirection: 'desc',
      date: this.selectedDate,
    };
  }

  get allEvents(): ParsedEvent[] {
    return this.events.concat(
      this.excludedEvents,
      this.tmpEvents,
      this.slots,
      this.backgroundEvents,
    );
  }

  async getAllEvents() {
    this.currentContactBookings = [];
    const promises = [this.getEvents(), this.getConflicts(), this.getCurrentContactEvents()];
    await Promise.all(promises);
  }

  async getEvents() {
    if (!this.selectedDate) {
      return;
    }
    const config = {
      params: {
        status: ['confirmed', 'requested'],
        per_page: -1,
        orderBy: 'date',
        orderDirection: 'desc',
        date: this.selectedDate,
        resources: this.rawResources.map(resource => {
          return resource.id;
        }),
      },
    };

    const res: BPCGenericApiResponse = await this.$bpcApi
      .get(`${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/public-booking`, config)
      .toPromise();

    this.events = res.data.map(event => {
      if (event.contact_id && Number(event.contact_id) === Number(this.currentContactId)) {
        return this.parseBookingEventToCalendarOwnEvent(event);
      }
      return this.parseBookingEventToCalendarNotAvailableEvent(event);
    });
  }

  async getCurrentContactEvents() {
    const query = {
      status: ['confirmed', 'requested'],
      page: 1,
      per_page: 200,
      orderBy: 'date',
      orderDirection: 'desc',
      contact_id: this.currentContactId,
      date_from: moment()
        .subtract(7, 'days')
        .format('YYYY-MM-DD'),
      columns: [
        'id',
        'resource_id',
        'contact_id',
        'date',
        'start_time',
        'end_time',
        'center_id',
        'discipline_id',
      ],
      withRelations: ['resource', 'discipline', 'center', 'orders'],
    };

    const config = {
      params: query,
    };

    const res: BPCGenericApiResponse = await this.$bpcApi
      .get(`${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/public-booking`, config)
      .toPromise();

    this.currentContactBookings = res.data;
  }

  async getConflicts() {
    const config = {
      params: this.eventQuery,
    };

    const res: BPCGenericApiResponse = await this.$bpcApi
      .get(`${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/resource-conflicts`, config)
      .toPromise();
    this.excludedEvents = res.data.map(excludedEvent => {
      return this.parseBookingEventToCalendarNotAvailableEvent(excludedEvent);
    });
  }

  printDays(days: DaysInput) {
    const print = [];
    Object.keys(days).filter(day => {
      if (days[day]) {
        print.push(this.$t(`bookings.schedule_slot_ranges.${day}.label`));
      }
    });
    return print.toString().replaceAll(',', ', ');
  }

  /* Save booking */
  async saveBooking(data: Partial<BookingOrganizationForm>) {
    this.savingBooking = true;
    await this.$bpcApi
      .post(`${this.$bpcApiRouteHelper.getEntityPath('booking')}/public-organization`, data)
      .toPromise()
      .then(async res => {
        this.confirmedBookingEventData = res.data;
        this.$root.$emit('refresh-orders');
        this.onCloseConfirmBookingModal();
        this.$bvModal.show('success-booking-confirmation-modal');
        await this.getAllEvents();
        this.checkSlotsAvailability();
        this.savingBooking = false;
      })
      .catch(err => {
        this.$bvModal.hide('booking-confirmation-modal');
        this.$bvModal.msgBoxOk(this.$t(err.response.data.message).toString(), {
          title: this.$t('global.modals.public_booking.not_possible.title').toString(),
          okTitle: this.$t('global.buttons.close').toString(),
          okVariant: 'secondary',
        });
        this.savingBooking = false;
      });
    this.showConfirmationBookingForm = false;
  }

  /* Criterions for booking */
  get currentSlotDuration(): number {
    return this.$store.getters['centerSettings/centerSettings']?.default_slot_duration || 30;
  }

  get currentSlotMinTime(): string {
    return this.$store.getters['centerSettings/centerSettings']?.default_start_hour;
  }

  get currentSlotMaxTime(): string {
    return this.$store.getters['centerSettings/centerSettings']?.default_end_hour;
  }

  minBookingDateCriterionErrorMessage: string = null;
  maxBookingDateCriterionErrorMessage: string = null;
  minTimeBeforeTwoBookingsCriterionErrorMessage: number = 0;
  minTimeBeforeCancellationCriterionErrorMessage: number = 0;

  get minimumBookingDate(): Moment {
    if (this.mergedBookingOptions?.min_time_before_booking) {
      return moment().add(this.mergedBookingOptions.min_time_before_booking, 'minutes');
    }
  }

  get maximumBookingDate(): Moment {
    if (this.mergedBookingOptions?.max_time_before_booking) {
      return moment().add(
        Number(this.mergedBookingOptions.max_time_before_booking) * 24 * 60,
        'minutes',
      );
    }
  }

  get minTimeBetweenTwoBookings(): number {
    return 60 * (this.mergedBookingOptions?.min_time_between_two_bookings || 0);
  }

  resourceTodayStartTime(): string {
    const start = moment(`${this.selectedDate} ${this.currentSlotMinTime}`, 'YYYY-MM-DD HH:mm:ss');
    const startTimes = this.mergedBookingOptions.start_time.split([',']);
    let optionStartTime = null;
    if (startTimes.length === 1 || (startTimes.length > 0 && startTimes.length !== 7)) {
      optionStartTime = moment(startTimes[0], 'HH:mm');
    } else if (startTimes.length === 7) {
      const day = start.weekday();
      if (day === 0) {
        optionStartTime = moment(startTimes[6], 'HH:mm');
      } else {
        optionStartTime = moment(startTimes[day - 1], 'HH:mm');
      }
    }
    return optionStartTime ? optionStartTime.format('HH:mm') : start.format('HH:mm');
  }

  checkMinTimeBetweenTwoBookings(start: Moment): boolean {
    let isValid = true;
    const eventStartDate = moment(`${this.selectedDate} ${start.format('HH:mm:ss')}`);

    this.currentContactBookings.forEach(event => {
      const diffTime = eventStartDate.diff(moment(`${event.date} ${event.end_time}`), 'minutes');
      if (this.minTimeBetweenTwoBookings > 0 && diffTime < this.minTimeBetweenTwoBookings) {
        isValid = false;
      }
    });

    this.minTimeBeforeTwoBookingsCriterionError = !isValid;

    if (!isValid) {
      this.minTimeBeforeTwoBookingsCriterionErrorMessage = this.minTimeBetweenTwoBookings;
    } else {
      this.minTimeBeforeTwoBookingsCriterionErrorMessage = 0;
    }

    return isValid;
  }

  checkMinAndMaxDateCriterion(start: Moment, end: Moment): boolean {
    const now = moment();
    const isValid =
      start.isAfter(now) &&
      start.isSameOrAfter(this.minimumBookingDate) &&
      end.isSameOrBefore(this.maximumBookingDate);
    this.minAndMaxDateCriterionError = !isValid;

    if (!isValid) {
      this.minBookingDateCriterionErrorMessage = this.minimumBookingDate?.format(
        'DD/MM/YYYY HH:mm',
      );
      this.maxBookingDateCriterionErrorMessage = this.maximumBookingDate?.format(
        'DD/MM/YYYY HH:mm',
      );
    } else {
      this.minBookingDateCriterionErrorMessage = null;
      this.maxBookingDateCriterionErrorMessage = null;
    }

    return isValid;
  }

  canBeBooked(start: Moment, end: Moment): boolean {
    return (
      this.checkMinAndMaxDateCriterion(start, end) && this.checkMinTimeBetweenTwoBookings(start)
    );
  }

  /* Parse slots and events */
  parseSlotAvailableToCalendar(date, start, end, resource = null) {
    return {
      start: this.formatDateTime(date, start),
      end: this.formatDateTime(date, end),
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      resourceId: `${resource.id}`,
      className: ['slot-available'],
      display: 'background',
    };
  }

  parseSlotNotAvailableToCalendar(date, start, end, resource = null) {
    return {
      start: this.formatDateTime(date, start),
      end: this.formatDateTime(date, end),
      backgroundColor: '#eee', //'#cccccc', // 'grey',
      borderColor: '#eee', //'#cccccc', // 'grey',
      //backgroundColor: '#cccccc', // 'grey',
      //borderColor: '#cccccc', // 'grey',
      resourceId: `${resource.id}`,
      className: ['slot-not-available'],
      display: 'background',
      extendedProps: {
        resourceId: `${resource.id}`,
      },
    };
  }

  parseBookingEventToCalendarNotAvailableEvent(event: BookingEvent): ParsedEvent {
    return {
      id: event?.id,
      title: this.$t('global.booking_events.not_available'),
      start: this.formatDateTime(event.date, event.start_time),
      end: this.formatDateTime(event.date, event.end_time),
      backgroundColor: '#aaa', //'#0d3a5d', //'#d92550',
      borderColor: '#aaa', //'#0d3a5d', //'#d92550',
      resourceId: `${event?.resource_id}`,
      className: ['unavailable-event'],
      // display: 'background',
    };
  }

  parseBookingEventToCalendarOwnEvent(event: BookingEvent): ParsedEvent {
    const request =
      event.status === 'requested' ? ` [${this.$t('global.booking_events.REQUEST')}]` : '';
    return {
      id: event.id,
      title: (event.name || this.$t('global.booking_events.my_booking')) + request,
      start: this.formatDateTime(event.date, event.start_time),
      end: this.formatDateTime(event.date, event.end_time),
      backgroundColor: '#00b0e1', //'#0d3a5d',
      borderColor: '#00b0e1', //'#0d3a5d',
      resourceId: `${event.resource_id}`,
      className: ['my-event', `booking-status-${event.status}`],
      resource: event.resource,
      discipline: event.discipline,
      // display: 'background',
    };
  }

  formatDateTime(date, time): string {
    return moment(`${date} ${time}`).toISOString();
  }

  async getSlotPricesCategories(): Promise<void> {
    if (!this.eventQuery.date) {
      console.warn('No date selected');
      return;
    }
    const params = {
      date: this.eventQuery.date,
      with_resources: true,
    };
    const res = await this.$bpcApi
      .get(this.$bpcApiRouteHelper.getEntityPath('schedule-slot-ranges') + '/ranges-by-date', {
        params: params,
      })
      .toPromise();
    this.backgroundEvents = this.addSlotCategoriesAsBackgroundEvents(res.data, params.date);
  }

  addSlotCategoriesAsBackgroundEvents(
    scheduleSlotRangeCollection: ScheduleSlotRange[],
    date: string,
  ): ParsedEvent[] {
    return scheduleSlotRangeCollection.reduce((backgroundEvents: ParsedEvent[], slot) => {
      const onlineBookingResourceIds = slot.online_booking_resources;
      const onlineBookingSchedules = this.addBackgroundEventsFromSlot(
        slot,
        onlineBookingResourceIds,
        date,
      );
      backgroundEvents = backgroundEvents.concat(onlineBookingSchedules);

      // Apply the default schedule if the resource has not online schedule
      const defaultResourceIds = slot.default_resources.filter(
        resourceId => !onlineBookingResourceIds.includes(resourceId),
      );
      const defaultBookingSchedules = this.addBackgroundEventsFromSlot(
        slot,
        defaultResourceIds,
        date,
      );

      backgroundEvents = backgroundEvents.concat(defaultBookingSchedules);
      return backgroundEvents;
    }, []);
  }

  addBackgroundEventsFromSlot(
    slot: ScheduleSlotRange,
    resourceIds: number[],
    date: string,
  ): ParsedEvent[] {
    return resourceIds.map(resourceId => {
      return {
        start: this.formatDateTime(date, slot.start_time),
        end: this.formatDateTime(date, slot.end_time),
        backgroundColor: `${slot.color}`,
        borderColor: `${slot.color}`,
        display: 'background',
        resourceId: resourceId,
      };
    });
  }

  generateBookingEventCancelData(
    id: number,
    start: Moment,
    end: Moment,
    resourceId: number,
    resource: string,
    discipline: string,
    isMultiplayer: boolean,
    numberOfPlayers: number,
  ): PublicBookingEventData {
    return {
      id: id,
      start_time: start.format('HH:mm:ss'),
      date: start.format('YYYY-MM-DD'),
      end_time: end.format('HH:mm:ss'),
      resourceId: resourceId,
      resource: resource,
      discipline: discipline,
      is_multiplayer: isMultiplayer,
      number_of_players: numberOfPlayers,
    };
  }

  generateBookingData(
    start: Moment,
    end: Moment,
    resourceId: number | string,
    resource = null,
  ): PublicBookingFormData {
    return {
      start_time: start.format('HH:mm:ss'),
      date: start.format('YYYY-MM-DD'),
      end_time: end.format('HH:mm:ss'),
      resource_id: Number(resourceId),
      discipline_id: null,
      subscription_category_id: null,
      extra: {
        resource: resource,
        discipline: null,
      },
    };
  }

  parseBookingDataToCalendarTmpEvent(event: PublicBookingFormData): ParsedEvent {
    return {
      id: null,
      title: this.$t('global.booking_events.new_booking'),
      start: this.formatDateTime(event.date, event.start_time),
      end: this.formatDateTime(event.date, event.end_time),
      backgroundColor: '#cccccc', // 'grey',
      borderColor: '#cccccc', // 'grey',
      resourceId: `${event.resource_id}`,
      className: ['tmp-event'],
      booking_type: 'tmp',
      // display: 'background',
    };
  }

  allLoadedEventsByResource(resourceId: number | string): ParsedEvent[] {
    return this.allEvents.filter(
      event => Number(event.resourceId) === Number(resourceId) && event.display !== 'background',
    );
  }
  allBackgroundEventsByResource(resourceId: number | string): ParsedEvent[] {
    return this.backgroundEvents.filter(event => Number(event.resourceId) === Number(resourceId));
  }

  /* Calendar events */
  onEventClicked(event) {
    const bookingEvent = event.event;
    console.log('EVENT CLICKED ', bookingEvent);
    if (bookingEvent.classNames.includes('my-event')) {
      this.bookingEventData = this.generateBookingEventCancelData(
        bookingEvent.id,
        moment(bookingEvent.start),
        moment(bookingEvent.end),
        bookingEvent.extendedProps.resource?.id,
        bookingEvent.extendedProps.resource?.name,
        bookingEvent.extendedProps.discipline?.name,
        bookingEvent.extendedProps.isMultiplayer,
        bookingEvent.extendedProps.numberOfPlayers,
      );
      this.showBookingEventDetailModal();
    }
  }

  onRepetitiveChange(repetitive: boolean) {
    if (!repetitive) {
      this.formModel.start_date = this.bookingData.date;
      this.formModel.end_date = this.bookingData.date;
    }
  }

  onDateSelect(info: DateSelectArg) {
    const savedMergedBookingOptions = this.mergedBookingOptions;
    this.bookingData = null;
    const resourceId = info.resource?.id || info['extendedProps'].resourceId;
    this.mergedBookingOptions = extractOptions(this.bookingOptions, {
      contacts: this.currentContactId,
      resources: resourceId,
    }); // To use in the booking;
    const start = moment(info.startStr);
    const end = moment(info.endStr);

    if (this.canBeBooked(start, end)) {
      // confirm the datas
      this.bookingData = this.generateBookingData(start, end, resourceId, info.resource);
      this.tmpEvents.push(this.parseBookingDataToCalendarTmpEvent(this.bookingData));

      // Preset data
      this.formModel.start_date = this.bookingData.date;
      this.formModel.end_date = this.bookingData.date;
      this.formModel.start_time = this.bookingData.start_time;
      this.formModel.end_time = this.bookingData.end_time;
      this.formModel.resource_id = this.bookingData.resource_id;
      this.formModel.resources = [this.bookingData.resource_id];
      this.formModel.discipline_id = this.bookingData.discipline_id;

      const weekday = moment(this.bookingData.date)
        .format('ddd')
        .toLowerCase();
      this.formModel.days[weekday] = true;

      this.showBookingConfirmationModal();
    } else {
      this.showBookingNotPosibleModal();
    }
    this.mergedBookingOptions = savedMergedBookingOptions;
  }

  onSlotNotAvailable(event) {
    const savedMergedBookingOptions = this.mergedBookingOptions;
    this.bookingData = null;
    const resourceId = event['extendedProps'].resourceId;
    this.mergedBookingOptions = extractOptions(this.bookingOptions, {
      contacts: this.currentContactId,
      resources: resourceId,
    }); // To use in the booking;
    const start = moment(event.startStr);
    const end = moment(event.endStr);

    if (!this.canBeBooked(start, end)) {
      this.showBookingNotPosibleModal();
    }
    this.mergedBookingOptions = savedMergedBookingOptions;
  }

  /* Other methods */
  cleanTmpEvents() {
    this.tmpEvents = [];
  }

  checkSlotsAvailability() {
    this.slotsChecked = false;
    const checkSlots = [];
    const savedMergedBookingOptions = this.mergedBookingOptions;

    // Check all resources
    this.rawResources.forEach(resource => {
      // Default hours to open and close center
      const start = moment(
        `${this.selectedDate} ${this.currentSlotMinTime}`,
        'YYYY-MM-DD HH:mm:ss',
      );
      const end = moment(`${this.selectedDate} ${this.currentSlotMaxTime}`, 'YYYY-MM-DD HH:mm:ss');
      const totalSlots = Math.ceil(end.diff(start, 'minutes') / this.currentSlotDuration);
      this.mergedBookingOptions = extractOptions(this.bookingOptions, {
        contacts: this.currentContactId,
        resources: resource.id,
      });
      // Check all slots by resource
      for (let init = 0; init < totalSlots; init++) {
        const endSlot = moment(start).add(this.currentSlotDuration, 'minutes');

        // Start and end of possible event
        const startEvent = moment(start);
        const endEvent = moment(startEvent);
        endEvent.add(this.currentSlotDuration, 'minutes');
        if (this.canBeBooked(startEvent, endEvent)) {
          checkSlots.push(
            this.parseSlotAvailableToCalendar(
              this.selectedDate,
              start.format('HH:mm'),
              endSlot.format('HH:mm'),
              resource,
            ),
          );
        } else {
          checkSlots.push(
            this.parseSlotNotAvailableToCalendar(
              this.selectedDate,
              start.format('HH:mm'),
              endSlot.format('HH:mm'),
              resource,
            ),
          );
        }
        start.add(this.currentSlotDuration, 'minutes');
      }
    });

    this.slots = checkSlots;
    this.slotsChecked = true;
    this.mergedBookingOptions = savedMergedBookingOptions;
  }

  @Watch('selectedDate')
  async onSelectedDateChange(val) {
    await this.getSlotPricesCategories();
    if (val) {
      await this.getAllEvents();
      this.checkSlotsAvailability();
    }
  }
}
